import { WorkCase } from '@/data/types';

const aiVideoTranscriptionAutomation: WorkCase = {
  id: "video-transcription",
  slug: "ai-video-transcription-automation",
  title: "AI Video-Transkription",
  description: "Automatisierte Lösung zur Erstellung barrierefreier Untertitel für tausende Produktvideos eines Online-Shops.",
  tags: ["Python", "OpenAI Whisper", "OpenCV", "FFmpeg", "Web Crawling", "AI", "Subtitles", "BFSG"],
  image: "/assets/img/work/ai_transcripts_python.webp",
  articleType: "CASE STUDY",
  date: "2025-03",
  detailContent: {
    subtitle: "KI-gestützte Untertitelung und Barrierefreiheit für E-Commerce-Videoinhalte",
    challenge: [
      {
        text: "Für die Produktvideos eines Online-Shops sollte die Barrierefreiheit durch Transkription und Untertitelung verbessert werden, eine Anforderung, die im Zuge des Barrierefreiheitsstärkungsgesetzes (BFSG) an Relevanz gewonnen hat."
      },
      {
        text: "Der Umfang war erheblich: tausende Produktvideos mussten identifiziert, abgerufen und mit präzisen Untertiteln versehen werden. Eine manuelle Bearbeitung wäre viel zu zeit- und kostenintensiv gewesen. Zudem lagen die Videodateien nicht in einer zentralen Übersicht vor, sondern mussten erst aufgefunden werden."
      },
      {
        text: "Zusätzliche Herausforderungen umfassten:"
      },
      {
        listing: [
          "Das Fehlen einer zentralen Videoübersicht oder klaren Struktur der Speicherorte",
          "Schutzmaßnahmen gegen automatisierte Zugriffe, die einen schonenden, rate-limitierten Abruf erforderten",
          "Die Notwendigkeit wortgenauer Untertitel für optimale Barrierefreiheit",
          "Benötigte Anpassungen für Markennamen und branchenspezifische Fachbegriffe"
        ]
      }
    ],
    solution: [
      {
        text: "Ich entwickelte eine durchgängige Pipeline, die das Problem in mehreren Schritten löste:"
      },
      {
        subheadline: "1. Crawler für Videoidentifikation",
        text: "Durch Analyse der Websitestruktur stellte ich fest, dass die Produktvideos einem festen Namensschema anhand der jeweiligen Produktnummer folgten. Ich entwickelte einen Python-Crawler, der den Katalog systematisch abprüfte und so über tausend Videos identifizierte, ohne dass eine zentrale Übersicht nötig war."
      },
      {
        subheadline: "2. Schonendes Download-Management",
        text: "Für den Abruf implementierte ich ein sequentielles, rate-limitiertes Verfahren mit verzögerten Anfragen und sauberem Session-Handling. So wurden die Videos zuverlässig und serverschonend heruntergeladen."
      },
      {
        subheadline: "3. Audio-Extraktion und KI-Transkription",
        text: "Mit FFmpeg extrahierte ich die Audiospuren aus den Videos. Für die Transkription nutzte ich einen spezialisierten Fork von OpenAI's Whisper (whisper-timestamped), der wortgenaue Zeitstempel liefert, eine Funktion, die sonst nur in der kostenpflichtigen OpenAI API verfügbar ist."
      },
      {
        subheadline: "4. Generierung von Untertiteldateien",
        text: "Die wortgenauen Transkriptionen wandelte ich in SRT- und VTT-Formate um. Dabei implementierte ich einen intelligenten Algorithmus zur Segmentierung der Untertitel, der sicherstellte, dass sie optimal auf den Bildschirm passen und synchron mit dem Gesprochenen erscheinen."
      },
      {
        subheadline: "5. Automatische Thumbnail-Generierung",
        text: "Mit OpenCV analysierte ich die Videos, um qualitativ hochwertige Frames als Thumbnails zu extrahieren. Der Algorithmus berücksichtigte Faktoren wie Farbvielfalt und Detailreichtum, um sicherzustellen, dass die Thumbnails repräsentativ und ansprechend sind."
      }
    ],
    results: [
      {
        text: "Die entwickelte Pipeline erzielte beeindruckende Ergebnisse:"
      },
      {
        listing: [
          "Erfolgreiche Identifikation und Verarbeitung von über tausend Produktvideos",
          "Automatische Erstellung präziser Untertitel mit einer geschätzten Fehlerquote von unter 5% bei normalem Text",
          "Signifikante Verbesserung der Barrierefreiheit für hörgeschädigte Kunden",
          "Enorme Zeit- und Kostenersparnis im Vergleich zu einer manuellen Transkription",
          "Wiederverwendbare Pipeline, die für neue Produktvideos eingesetzt werden kann"
        ]
      },
      {
        text: "Besonders hervorzuheben ist die Qualität der automatisch generierten Untertitel und die Präzision der Wort-Timings, die ein synchrones Leseerlebnis ermöglichen. Die einzige Einschränkung lag bei der Erkennung spezieller Markennamen und Fachbegriffe, die in zukünftigen Versionen durch angepasste Wörterbücher verbessert werden könnte."
      }
    ],
    technologies: [
      "Python 3.10",
      "OpenAI Whisper (Fork)",
      "FFmpeg",
      "OpenCV",
      "WebVTT & SRT",
      "Requests & BeautifulSoup"
    ],
    codeExamples: [
      {
        language: "python",
        code: `class PreciseSubtitleGenerator:
    def __init__(self, input_dir="transcribed_videos", model_name="large-v3", device="auto", vad_method="auditok"):
        """
        Initialisiert den präzisen Subtitle-Generator mit whisper-timestamped.

        input_dir: Das Basis-Verzeichnis mit den transkribierten Videos
        model_name: Das zu verwendende Whisper-Modell
        device: Gerät für die Verarbeitung ('auto', 'cpu', oder 'mps')
        vad_method: VAD-Methode ("silero", "auditok", "auditok:v3.1", False)
        """
        self.input_dir = input_dir
        self.model_name = model_name
        self.vad_method = vad_method
        self.model = None

        # VAD-Kompatibilität prüfen
        if vad_method == "silero" and not has_vad_deps:
            print("Warnung: Silero VAD-Abhängigkeiten fehlen. Verwende auditok stattdessen.")
            self.vad_method = "auditok"

        # Automatische Geräteauswahl - Versuche zuerst CPU als sicherste Option
        self.device = "cpu"
`,
        description: "Die PreciseSubtitleGenerator-Klasse implementiert die Kernfunktionalität für die automatische Untertitelgenerierung mit Whisper. Sie unterstützt verschiedene Hardware-Konfigurationen und VAD-Methoden."
      }
    ],
    images: [
      {
        src: "/assets/img/work/transcription_flow.webp",
        zoomSrc: "/assets/img/work/transcription_flow.svg",
        thumbSrc: "/assets/img/work/transcription_flow.webp",
        alt: "Transcription Workflow",
        type: "diagram",
        title: "Transcription Workflow",
        caption: "Der Workflow zeigt den Prozess der Transkription von Videos."
      },
      {
        type: "video",
        videoId: "01007470",
        title: "Produktvideo mit automatisch generierten Untertiteln",
        caption: "Ein Produktvideo mit wortgenauen Untertiteln verbessert die Barrierefreiheit für hörgeschädigte Nutzer"
      }
    ]
  }
};

export default aiVideoTranscriptionAutomation; 